// Profil-Nachbau F-12 (Bennys Freigabe 14.10.2026; AUFTRAG-PF-715; SPEC-WELLE-A WA-01 bis WA-06, WA-08): die Wörter der
// Standardansicht „Firmendaten“ nach Österreich – Name in CLAiRIS, Statusleiste, „Rechtliche Angaben“ mit den Rechtsform-Karten,
// „Sitz“, „Firmenbuch“ bzw. „Vereinsregister“, „Rechnungszustellung“ und das seitenweite „Rückgängig“ und „Speichern“. Wo der
// Auftrag von der Spec abweicht (fünf Adressen in einer Marken-Eingabe, Schalter für die abweichende Anschrift, „Noch nicht
// gewählt“), gilt der Auftrag. Reine Daten und Funktionen ohne React; die Prüfung selbst steht in billing/profileDraft.ts
// (profilFehler), mit denselben Sätzen am Feld wie der Server (backend billing/rechnungsdaten.ts RECHNUNGSDATEN_SAETZE).
// Vorhandene Wörter (FIRMENDATEN_TEXTE, VERWERFEN, PLZ_FEHLER, emailFehler) stehen nicht hier, sondern werden eingebunden.
// Geprüft in frontend/tests/welle-a-rechnungsdaten.test.mjs.
import type { Rechtsform } from '../billing/profileDraft';
import { emailFehler } from './feldFehler';

/** Die fünf Karten der Rechtsform in ihrer Reihenfolge (WA-02); null ist „Noch nicht gewählt“. F-24 (PF-TX-firma-1): je Karte
 *  eine kurze Zeile wie bei meinGPT. */
export const RECHTSFORM_OPTIONEN: readonly { wert: Rechtsform | null; titel: string; erklaerung: string }[] = [
  { wert: 'SOLE_TRADER', titel: 'Einzelunternehmen', erklaerung: 'Selbstständig, auch freie Berufe und e.U.' },
  { wert: 'COMPANY', titel: 'Gesellschaft', erklaerung: 'Im Firmenbuch eingetragen, etwa GmbH, FlexCo, AG, OG, KG oder Genossenschaft.' },
  { wert: 'PUBLIC_BODY', titel: 'Öffentliche Einrichtung', erklaerung: 'Gemeinde, Land, Bund, Schule oder andere Körperschaft.' },
  { wert: 'ASSOCIATION', titel: 'Verein', erklaerung: 'Im Vereinsregister, mit ZVR-Zahl.' },
  { wert: null, titel: 'Noch nicht gewählt', erklaerung: 'Bis dahin bleiben die Rechnungsdaten unvollständig.' },
];
export const rechtsformTitel = (wert: Rechtsform | null | undefined) => RECHTSFORM_OPTIONEN.find(option => option.wert === (wert ?? null))!.titel;

/** Das Register je Rechtsform (WA-03): Firmenbuch bei Gesellschaft und, freiwillig, beim eingetragenen Einzelunternehmen;
 *  Vereinsregister beim Verein; sonst keines (der Abschnitt entfällt). */
export type RegisterArt = 'FIRMENBUCH' | 'VEREINSREGISTER' | null;
export const registerArt = (wert: Rechtsform | null | undefined): RegisterArt =>
  wert === 'COMPANY' || wert === 'SOLE_TRADER' ? 'FIRMENBUCH' : wert === 'ASSOCIATION' ? 'VEREINSREGISTER' : null;
/** Pflicht für die Vollständigkeit (WA-06): das Firmenbuch nur bei der Gesellschaft, die ZVR-Zahl beim Verein. */
export const registerPflicht = (wert: Rechtsform | null | undefined) => wert === 'COMPANY' || wert === 'ASSOCIATION';
/** Die Kleinunternehmerregelung gibt es nur beim Sitz in Österreich und nicht für eine öffentliche Einrichtung (WA-03). */
export const kleinunternehmerSichtbar = (land: string | null | undefined, wert: Rechtsform | null | undefined) =>
  (land ?? '').trim().toUpperCase() === 'AT' && wert !== 'PUBLIC_BODY';

/** Die Vorschlagsliste der 16 Firmenbuchgerichte (WA-03). */
export const FIRMENBUCHGERICHTE: readonly string[] = ['Handelsgericht Wien', 'Landesgericht für Zivilrechtssachen Graz',
  ...['Eisenstadt', 'Feldkirch', 'Innsbruck', 'Klagenfurt', 'Korneuburg', 'Krems an der Donau', 'Leoben', 'Linz', 'Ried im Innkreis', 'Salzburg',
    'St. Pölten', 'Steyr', 'Wels', 'Wiener Neustadt'].map(ort => `Landesgericht ${ort}`)];

/** Die Sätze am Feld, wörtlich wie der Server (RECHNUNGSDATEN_SAETZE in backend billing/rechnungsdaten.ts). Die Postleitzahl
 *  nennt PLZ_FEHLER (billing/profileDraft.ts, seit PF-716-nach derselbe Satz wie plz am Server), die E-Mail-Adresse EMAIL_FEHLER
 *  (feldFehler.ts). */
export const RECHNUNGSDATEN_SAETZE = {
  firmenbuchnummer: 'Bitte die Firmenbuchnummer wie im Auszug eingeben, etwa FN 123456a.',
  firmenbuchgericht: 'Bitte das Firmenbuchgericht eingeben, etwa Landesgericht Innsbruck.',
  zvr: 'Die ZVR-Zahl hat neun oder zehn Ziffern.',
  steuernummer: 'Die Steuernummer hat neun Ziffern, etwa 12 345/6789.',
  uid: 'Eine österreichische UID beginnt mit ATU und hat danach 8 Ziffern.',
  adresszusatz: 'Der Adresszusatz ist eine Zeile mit höchstens 150 Zeichen.',
  doppelt: 'Diese Adresse steht schon in der Liste.',
  hoechstensVier: 'Höchstens vier weitere Adressen.',
  abweichend: 'Für die abweichende Rechnungsanschrift bitte ausfüllen.',
} as const;

export const RECHNUNGSDATEN_TEXTE = {
  // Name in CLAiRIS (F-12 Punkt 1); „CLAiRIS“ kommt aus brand.displayName (MK-1), siehe nameInTitel.
  nameHinweis: 'Unabhängig vom rechtlichen Namen auf Rechnungen.',
  // Statusleiste (F-12 Punkt 2), aus dem Stand des Servers.
  vollstaendig: 'Rechnungsdaten vollständig – bereit für die Rechnungsstellung.',
  marke: { VOLLSTAENDIG: 'Vollständig', UNVOLLSTAENDIG: 'Unvollständig' },
  // Rechtliche Angaben (F-12 Punkt 3, WA-01 bis WA-03).
  rechtlich: 'Rechtliche Angaben',
  rechtlichSatz: 'Diese Angaben erscheinen auf jeder Rechnung und jedem Vertrag.',
  rechtsform: 'Rechtsform',
  rechtlicherName: 'Rechtlicher Name',
  // F-24 (PF-TX-firma-1, SPEC-firma-b 2): sichtbar bleibt das Formatbeispiel; was der Name bewirkt, steht im Tooltip am Feld (tipp).
  rechtlicherNameHinweis: 'Wie im Firmenbuch oder Vereinsregister, etwa „Muster Steuerberatung GmbH“.',
  rechtlicherNameTipp: 'Er steht auf jeder neuen Rechnung und im AVV.',
  land: 'Land',
  landCode: 'Ländercode (zwei Buchstaben, z. B. FR)',
  bitteWaehlen: 'Bitte wählen',
  // F-23 (PF-NB-firma-2, Fehlersuche Nr. 40): in Österreich „UID-Nummer“, wie die Einleitung und der Satz des Servers.
  uid: 'UID-Nummer',
  uidHinweis: 'In Österreich ATU und 8 Ziffern, etwa ATU12345678.',
  steuernummer: 'Steuernummer',
  steuernummerPlatzhalter: '12 345/6789',
  steuernummerHinweis: 'Neun Ziffern, wie im Bescheid des Finanzamts.',
  // F-24 (PF-TX-firma-1): Rechtsgrundlage und Erklärung stehen im Tooltip hinter dem Kästchen, nicht mehr sichtbar darunter.
  kleinunternehmer: 'Kleinunternehmerregelung',
  kleinunternehmerTipp: 'Nach § 6 Abs. 1 Z 27 UStG weist Ihre Firma auf eigenen Rechnungen keine Umsatzsteuer aus. Die Rechnungen von CLAiRIS ändern sich dadurch nicht.',
  // Sitz (F-12 Punkt 4, WA-05 Punkt 1); F-24: der Weg zur abweichenden Anschrift im Tooltip am Abschnitt.
  sitz: 'Sitz',
  sitzSatz: 'Wie im Firmenbuch oder Vereinsregister.',
  sitzTipp: 'Eine abweichende Rechnungsanschrift tragen Sie unten unter „Rechnungszustellung“ ein.',
  strasse: 'Straße und Hausnummer',
  adresszusatz: 'Adresszusatz',
  adresszusatzPlatzhalter: 'Top 4',
  plz: 'Postleitzahl',
  ort: 'Ort',
  // Firmenbuch bzw. Vereinsregister (F-12 Punkt 5, WA-03).
  firmenbuch: 'Firmenbuch',
  vereinsregister: 'Vereinsregister',
  firmenbuchnummer: 'Firmenbuchnummer',
  firmenbuchnummerPlatzhalter: 'FN 123456a',
  firmenbuchnummerHinweis: 'Steht im Firmenbuchauszug.',
  firmenbuchgericht: 'Firmenbuchgericht',
  firmenbuchgerichtPlatzhalter: 'Landesgericht Innsbruck',
  firmenbuchgerichtHinweis: 'Das Gericht, bei dem Ihre Firma eingetragen ist.',
  zvr: 'ZVR-Zahl',
  zvrPlatzhalter: '123456789',
  zvrHinweis: 'Neun oder zehn Ziffern laut Vereinsregisterauszug.',
  // Rechnungszustellung (F-12 Punkt 6, WA-04, WA-05).
  zustellung: 'Rechnungszustellung',
  emails: 'E-Mail-Adressen für Rechnungen',
  // F-24 (PF-TX-firma-1): der Bedienhinweis bleibt sichtbar, die Erklärung steht im Tooltip hinter der Beschriftung.
  emailsHinweis: 'Mit Enter oder Komma hinzufügen, höchstens fünf.',
  emailsTipp: 'Die erste Adresse bekommt die Rechnung; alle weiteren erhalten zu jeder neuen Rechnung eine Nachricht von CLAiRIS.',
  emailsHoechstens: 'Höchstens fünf Adressen.',
  emailRechnung: 'Rechnung',
  bestellreferenz: 'Bestellreferenz',
  bestellreferenzHinweis: 'Steht auf jeder neuen Rechnung.',
  bestellreferenzTipp: 'Ihre Bestell- oder Auftragsnummer.',
  abweichend: 'Rechnungen an eine abweichende Anschrift senden',
  // F-24: als Tooltip hinter der Beschriftung des Schalters.
  abweichendHinweis: 'Etwa an die Buchhaltung an einem anderen Standort. Die Anschrift liegt im selben Land wie der Sitz.',
  zuHanden: 'Zu Handen',
  zuHandenPlatzhalter: 'Buchhaltung',
  // Werte ohne Schreibrecht (wie profilWerte in BillingPanel.tsx): leere Angaben als „–“.
  leer: '–',
  ja: 'Ja',
  nein: 'Nein',
  anschrift: 'Anschrift',
  rechnungsanschrift: 'Rechnungsanschrift',
  // Seitenweit (F-12 Punkt 8, WA-08); „Rückgängig“ setzt den ganzen Entwurf auf den gespeicherten Stand.
  rueckgaengig: 'Rückgängig',
  speichern: 'Speichern',
  gespeichert: 'Die Firmendaten sind gespeichert.',
  teilweise: 'Nicht alles wurde gespeichert. Bitte die markierten Angaben prüfen.',
  // Ein Feld, das der Server abgelehnt hat und dessen Regel die Oberfläche nicht kennt (400 nennt nur fields).
  bittePruefen: 'Bitte diese Angabe prüfen.',
  // Ein Feld, das inzwischen jemand anderer gespeichert hat (409 PROFILE_CONFLICT), wie im Rechnungsprofil.
  inzwischen: (wert: string | null) => `Inzwischen gespeichert: „${wert || 'leer'}“. Ihre Eingabe bleibt stehen.`,
  ueberschreibt: 'Erneutes Speichern überschreibt die gespeicherten Werte.',
  uebernehmen: 'Gespeicherte Werte übernehmen',
  // Wie im Rechnungsprofil (Paket 12 K12-8): gespeichert, aber nicht beim Zahlungsdienst; „Speichern“ versucht es erneut.
  syncGescheitert: 'Gespeichert. Die Übertragung an den Zahlungsdienst ist gescheitert. Bitte erneut speichern.',
  laden: 'Rechnungsdaten werden geladen …',
} as const;

/** Die Beschriftung eines freiwilligen Felds: „Steuernummer (optional)“; Pflichtfelder setzt Field mit pflicht („(Pflicht)“). */
export const optional = (wort: string) => `${wort} (optional)`;
/** „Name in CLAiRIS“ mit dem Produktnamen aus brand.displayName (MK-1). */
export const nameInTitel = (produkt: string) => `Name in ${produkt}`;
/** PF-716-nach-4: der Anker des Abschnitts „Sitz“ (…/firmendaten#sitz). Dorthin führen „Rechnungsanschrift eintragen“ und die Zeile
 *  der Einrichtungsliste; Statusleiste und rechtlicher Name stehen direkt darüber. */
export const SITZ_ANKER = 'sitz';
/** Die Marke einer Adresse: „{Adresse} entfernen“ als Name des Knopfs. */
export const entfernenLabel = (adresse: string) => `${adresse} entfernen`;

/** Der Stand des Servers (GET/PUT /billing/profile, WA-06). */
export type AbschnittStand = 'VOLLSTAENDIG' | 'UNVOLLSTAENDIG' | 'ENTFAELLT';
export type RechnungsdatenStand = { vollstaendig: boolean; fehlt: string[];
  abschnitte: { rechtliches: AbschnittStand; anschrift: AbschnittStand; register: AbschnittStand; zustellung: AbschnittStand } };
// F-23 (PF-NB-firma-2): das Land nur als Vorgabe (landNurVorgeschlagen) und die interne Organisation ohne Rechnungsteil. F-24
// (PF-TX-firma-1): „vorgeschlagen“ sagt der Hinweis am Feld; die Statuszeile nennt nur noch den Stand.
export const LAND_VORGABE = {
  status: 'Land (noch nicht gespeichert)',
  hinweis: 'Vorgeschlagen: Österreich. Übernommen wird es mit „Speichern“.',
} as const;
export const INTERN_OHNE_RECHNUNG = 'Die interne Organisation des Betriebs bekommt keine Rechnungen; Rechnungsangaben braucht sie nicht.';
/** Der Titel des Abschnitts, in dem für die interne Organisation INTERN_OHNE_RECHNUNG steht (statt der vier Rechnungsabschnitte). */
export const INTERN_OHNE_RECHNUNG_TITEL = 'Rechnungsangaben';

/** „Rechnungsdaten vollständig – bereit für die Rechnungsstellung.“ bzw. „Es fehlt noch: Rechtsform, Ort.“ Die Teile nennt der
 *  Server mit den Wörtern der Felder dieser Seite (STAND_BESCHRIFTUNG, seit PF-716-nach auch „Rechtlicher Name“). F-23
 *  (PF-NB-firma-2): mit landVorgeschlagen heißt der Teil „Land“ wie LAND_VORGABE.status, damit die Zeile dem Feld nicht widerspricht. */
export function statusSatz(stand: Pick<RechnungsdatenStand, 'vollstaendig' | 'fehlt'>, optionen?: { landVorgeschlagen?: boolean }): string {
  const fehlt = optionen?.landVorgeschlagen ? stand.fehlt.map(teil => (teil === RECHNUNGSDATEN_TEXTE.land ? LAND_VORGABE.status : teil)) : stand.fehlt;
  return stand.vollstaendig ? RECHNUNGSDATEN_TEXTE.vollstaendig : `Es fehlt noch: ${fehlt.join(', ')}.`;
}

/** „FN 123456a“ aus der gespeicherten Form „123456a“ (WA-03). */
export const firmenbuchAnzeige = (nummer: string | null | undefined) => nummer ? `FN ${nummer}` : null;
/** „12 345/6789“ aus neun gespeicherten Ziffern; eine andere Form bleibt, wie sie ist. */
export const steuernummerAnzeige = (ziffern: string | null | undefined) =>
  !ziffern ? null : /^\d{9}$/.test(ziffern) ? `${ziffern.slice(0, 2)} ${ziffern.slice(2, 5)}/${ziffern.slice(5)}` : ziffern;

/** Höchstens fünf Adressen für Rechnungen zusammen (F-12 Punkt 6): die erste ist die Rechnungs-E-Mail, die weiteren vier
 *  bekommen eine Nachricht. */
export const HOECHSTENS_ADRESSEN = 5;
/** Eine Adresse der Marken-Eingabe hinzufügen (Enter oder Komma): gültig, ohne Doppel (Groß- und Kleinschreibung zählt nicht),
 *  höchstens fünf. Leer ändert nichts. Die Liste bleibt bei einem Fehler, wie sie war. */
export function adresseHinzufuegen(liste: readonly string[], eingabe: string): { liste: string[]; fehler: string | null } {
  const adresse = eingabe.trim().replace(/,+$/, '').trim();
  if (!adresse) return { liste: [...liste], fehler: null };
  const ungueltig = emailFehler(adresse);
  if (ungueltig) return { liste: [...liste], fehler: ungueltig };
  if (liste.some(wert => wert.toLowerCase() === adresse.toLowerCase())) return { liste: [...liste], fehler: RECHNUNGSDATEN_SAETZE.doppelt };
  if (liste.length >= HOECHSTENS_ADRESSEN) return { liste: [...liste], fehler: RECHNUNGSDATEN_TEXTE.emailsHoechstens };
  return { liste: [...liste, adresse], fehler: null };
}

/** Zusätzliche Stichwörter der Suche für den Eintrag „Firmendaten“ (AUFTRAG-PF-715 4.6). F-23 (PF-NB-firma-2): „UID-Nummer“ dazu,
 *  „Umsatzsteuer-ID“ bleibt als Suchwort. */
export const FIRMENDATEN_STICHWOERTER: readonly string[] = ['Rechtsform', 'Firmenbuch', 'FN', 'ZVR', 'Steuernummer', 'UID', 'UID-Nummer', 'Umsatzsteuer-ID', 'Kleinunternehmer',
  'Sitz', 'Adresszusatz', 'Rechnungs-E-Mail', 'Bestellreferenz', 'abweichende Anschrift'];
